import {
  ButtonHTMLAttributes,
  forwardRef,
  useContext,
  ComponentPropsWithoutRef,
  ReactNode,
  MouseEvent,
  useId,
  useRef,
  useState,
} from "react";
import cx from "classnames";
import { MenuContext } from "./Menu";
import styles from "./Menu.module.scss";
import { Icon, IconProps } from "../Icon";
import { useMergeRefs } from "../../hooks";
import { MenuLegacyItem } from "../MenuLegacy";
import { supportsPopover } from "../../internal/functions/supportsPopover";

/**
 * Props for the MenuItem component
 * @extends ComponentPropsWithoutRef<"button">
 */
export type MenuItemProps = {
  /**
   * Icon to display next to the menu item label
   */
  icon?: IconProps["svg"];
  /**
   * The label text or content for the menu item
   */
  label: ReactNode;
  /**
   * Whether the menu item is disabled
   * @default false
   */
  disabled?: boolean;
} & ComponentPropsWithoutRef<"button">;

/**
 * MenuItem component for individual menu options within a Menu.
 *
 * Features:
 * - Supports icons and labels
 * - Keyboard navigation support
 * - Disabled state handling
 * - Automatic menu closing on selection
 * - Fully accessible with proper ARIA attributes
 * - Focus management and tab index handling
 * - Click and mouse enter event handling
 * - Legacy popover support with fallback
 *
 * @example
 * <MenuItem
 *   label="Edit Profile"
 *   icon={EditIcon}
 *   onClick={() => console.log('Edit profile clicked')}
 * />
 */
export const MenuItem = forwardRef<
  HTMLButtonElement,
  MenuItemProps & ButtonHTMLAttributes<HTMLButtonElement>
>((props, ref) => {
  const {
    label,
    disabled,
    icon,
    className,
    onClick,
    onMouseEnter,
    id: idProp,
    ...rest
  } = props;
  const menuContext = useContext(MenuContext);
  const uid = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const combinedRef = useMergeRefs([buttonRef, ref]);
  const [tabIndex, setTabIndex] = useState(-1);
  const itemCx = cx([styles["item"]], className);
  const onClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onClick?.(e);
    menuContext?.closeRootMenu();
  };
  const onMouseEnterHandler = (e: MouseEvent<HTMLButtonElement>) => {
    onMouseEnter?.(e);
    buttonRef.current?.focus();
  };

  const onFocusHandler = () => {
    setTabIndex(0);
  };
  const onBlurHandler = () => {
    setTabIndex(-1);
  };

  if (!supportsPopover()) return <MenuLegacyItem {...props} ref={ref} />;

  return (
    <button
      {...rest}
      ref={combinedRef}
      type="button"
      role="menuitem"
      data-anv="menu-item"
      id={idProp ?? `menuitem-${uid.replace(/:/g, "")}`}
      className={itemCx}
      tabIndex={tabIndex}
      aria-disabled={disabled}
      onClick={onClickHandler}
      onMouseEnter={onMouseEnterHandler}
      onFocus={onFocusHandler}
      onBlur={onBlurHandler}
    >
      {icon ? <Icon inherit aria-hidden svg={icon} /> : null}
      {label}
    </button>
  );
});

MenuItem.displayName = "MenuItem";
